
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, ArrowRight, Inbox, MailOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useNotifications } from '@/hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

const NotificationDropdown = () => {
  const navigate = useNavigate();
  const { notifications, loading, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [onlyUnread, setOnlyUnread] = useState(false);

  const visibleNotifications = useMemo(() => {
    if (!onlyUnread) return notifications;
    return notifications.filter(n => !n.read);
  }, [notifications, onlyUnread]);

  // Click sulla notifica: marca come letta + naviga al link se presente
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleNotificationClick = (notification: any) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'success': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-blue-500';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), { 
      addSuffix: true, 
      locale: it 
    });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-[10px] font-medium text-white flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold">Notifiche</h4>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="text-xs"
              >
                <CheckCheck className="h-3 w-3 mr-1" />
                Segna tutte come lette
              </Button>
            )}
          </div>

          {/* Filtro tutte/non lette: visibile solo se ci sono notifiche. */}
          {notifications.length > 0 && (
            <div className="flex gap-1 -mt-1">
              <Button
                variant={!onlyUnread ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={() => setOnlyUnread(false)}
              >
                <Inbox className="h-3 w-3" />
                Tutte
                <span className="ml-0.5 text-[10px] text-muted-foreground tabular-nums">({notifications.length})</span>
              </Button>
              <Button
                variant={onlyUnread ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={() => setOnlyUnread(true)}
                disabled={unreadCount === 0}
              >
                <MailOpen className="h-3 w-3" />
                Non lette
                <span className="ml-0.5 text-[10px] text-muted-foreground tabular-nums">({unreadCount})</span>
              </Button>
            </div>
          )}
          
          {loading ? (
            <div className="py-8 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Caricamento...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-8 text-center">
              <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-sm text-muted-foreground mb-2">Nessuna notifica</p>
              <p className="text-xs text-muted-foreground">
                Le tue notifiche appariranno qui quando disponibili
              </p>
            </div>
          ) : visibleNotifications.length === 0 ? (
            <div className="py-8 text-center">
              <MailOpen className="h-10 w-10 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Nessuna notifica non letta</p>
              <Button
                variant="link"
                size="sm"
                className="mt-2 text-xs"
                onClick={() => setOnlyUnread(false)}
              >
                Mostra tutte
              </Button>
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {visibleNotifications.map((notification) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const link = (notification as any).link as string | undefined;
                const isClickable = !!link;
                return (
                  <div
                    key={notification.id}
                    onClick={isClickable ? () => handleNotificationClick(notification) : undefined}
                    className={`p-3 rounded-lg border transition-colors group ${
                      !notification.read
                        ? 'bg-muted/50 border-primary/20'
                        : 'bg-background hover:bg-muted/20'
                    } ${isClickable ? 'cursor-pointer hover:border-primary/40' : ''}`}
                  >
                    <div className="flex space-x-3">
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${getTypeColor(notification.type)}`}></div>
                      <div className="flex-1 space-y-1 min-w-0">
                        <div className="flex items-start justify-between gap-1">
                          <p className="text-sm font-medium">{notification.title}</p>
                          <div className="flex items-center gap-0.5 shrink-0">
                            {!notification.read && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }}
                                className="h-6 w-6 p-0"
                                title="Segna come letta"
                              >
                                <Check className="h-3 w-3" />
                              </Button>
                            )}
                            {isClickable && (
                              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">{notification.message}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatTimeAgo(notification.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationDropdown;
